<!-- Institute of Design and Innovation (email idi@unm.edu). -->

<!-- Appointment information webpage in modal view -->
<template>
  <av-modal class="appointment-info-modal" :visible="visible && appointment" @close="close">
    <div class="modal-header">
      {{ isBeingRescheduled ? 'Reschedule' : '' }}
      {{ isBeingReassigned ? 'Reassign' : '' }}
      {{ isEditingAppointmentForm ? 'Editing' : '' }}
      {{ appointment.appointmentType.name }}
      <div class="time-info">
        <i class="fal fa-calendar"></i> {{ formatTime(appointment.start, 'MMM do yyyy') }}{{ appointment.appointmentType.isTask? '' : formatTime(appointment.start,  ' @ hh:mma') }}
      </div>
    </div>

    <!-- Normal Display -->
    <template v-if="!isBeingReassigned && !isBeingRescheduled && !isEditingAppointmentForm">
      <div class="reviewer"><span>Reviewer:</span><span>{{ appointment.reviewer.firstName + ' ' + appointment.reviewer.lastName }}</span></div>
      <div class="modal-content">
        <div class="intake-form-responses">
          <p>
            <label class="av-label">PI Name</label>
            {{ `${appointment.piFirstName} ${appointment.piLastName}` }}
          </p>
          <p>
            <label class="av-label">PI Email</label>
            {{ appointment.piEmail }}
          </p>
          <p>
            <label class="av-label">PI Phone</label>
            {{ appointment.piPhone }}
          </p>
          <p v-if="appointment.additionalContacts.length > 0">
            <label class="av-label">Additional Contacts</label>
            <span v-for="contact of appointment.additionalContacts" :key="contact.name">
              {{ contact.name }}{{ contact.phone !== '' ? `, ${contact.phone}` : '' }}{{ contact.email !== '' ? `, ${contact.email}` : '' }}<br>
            </span>
          </p>
          <!-- <p>
            <label class="av-label">Proposal Number Type</label>
            {{ appointment.proposalNumberType }}
          </p> -->
          <p>
            <label class="av-label">Proposal Record Number</label>
            {{ proposalNumberDisplay }}
          </p>
          <p>
            <label class="av-label">Review Deadline</label>
            {{ formatTime(appointment.reviewDeadline, 'MMM do yyyy') }}
          </p>
          <p>
            <label class="av-label">Scheduled On</label>
            {{ formatTime(appointment.scheduledOn, 'MMM do yyyy') }}
          </p>
          <p v-for="response of appointment.intakeFormResponse" :key="response.id">
            <label class="av-label">{{ response.label }}</label>
            <template v-if="response.type !== 'checkbox'">
              {{ response.value !== '' ? response.value : 'NA' }}
            </template>
            <template v-if="response.type === 'checkbox'">
              {{ response.value === 'true' ? 'Yes' : 'No' }}
            </template>
          </p>
        </div>
      </div>
      <div class="modal-actions" v-if="isAdmin || (isReviewer && appointment.reviewer.id == userId)">
        <a class="shaded red" @click="cancel"><i class="fal fa-calendar-times"></i> Cancel</a>
        <a v-if="isAdmin" class="shaded black" @click="beginEditingIntakeForm"><i class="fal fa-edit"></i> Edit</a>
        <a v-if="isAdmin" class="shaded black" @click="beginReassign" v-is-admin><i class="fal fa-user"></i> Reassign</a>
        <a class="shaded black" @click="isBeingRescheduled = true"><i class="fal fa-calendar"></i> Reschedule</a>
      </div>
    </template>

    <!-- Editing Intake Form -->
    <template v-if="isEditingAppointmentForm">
      <div class="modal-content">
        <intake-form
          :intakeForm="intakeForm"
          :currentResponse="formattedIntakeFormResponse"
          @form-updated="newIntakeFormResponse = $event"
        ></intake-form>
      </div>

      <div class="modal-actions">
        <a class="shaded red" @click="isEditingAppointmentForm = false">Back</a>
        <a class="shaded black" @click="saveIntakeForm" :class="{ disabled: !newIntakeFormResponse.isValid }">Save</a>
      </div>
    </template>

    <!-- Reassigning -->
    <template v-if="isBeingReassigned">
      <div class="re-assign-insctruction">
        <template v-if="!reassignUser">Select a reviewer:</template>
        <template v-if="reassignUser">Are you sure you want to reassign this appointment?</template>
      </div>
      <div v-if="!reassignUser" class="select-new-reviewer">
        <div class="reassign-selection" v-for="reviewer of sortedAvailableReviewers" :key="reviewer.id" @click="reassignUser = reviewer">
          <div class="reviewer-info">
            <span>{{ reviewer.firstName + ' ' + reviewer.lastName }}</span>
            <span class="workload-ind">{{ workloadInHours(reviewer.workload) }}</span>
          </div>
          <div class="reassign-warnings">
            <p class="conflicts" v-if="reviewer.hasExternalConflicts"><i class="fal fa-exclamation-triangle"></i> This reviewer has conflicts at this time.</p>
            <p class="no-availability" v-if="!reviewer.availableForSlot && !appointment.appointmentType.isTask"><i class="fal fa-exclamation-circle"></i> This reviewer does not have regular availability for this review and time.</p>
          </div>
        </div>
      </div>

      <div v-if="reassignUser" class="modal-content confirm-reassign">
        <div class="reviewer-info">
          <span>{{ reassignUser.firstName + ' ' + reassignUser.lastName }}</span>
          <span class="workload-ind">{{ workloadInHours(reassignUser.workload) }}</span>
        </div>
        <div class="reassign-warnings">
          <p class="conflicts" v-if="reassignUser.hasExternalConflicts"><i class="fal fa-exclamation-triangle"></i> This reviewer has conflicts at this time.</p>
          <p class="no-availability" v-if="!reassignUser.availableForSlot && !appointment.appointmentType.isTask"><i class="fal fa-exclamation-circle"></i> This reviewer does not have regular available for this review and time.</p>
        </div>
      </div>

      <div class="modal-actions">
        <a v-if="!reassignUser" class="shaded red" @click="resetReassign">Cancel</a>
        <a v-if="reassignUser" class="shaded red" @click="reassignUser = null">Back</a>
        <a v-if="reassignUser" class="shaded black" @click="reassign"  :class="{ disabled: !isActive }">Reassign</a>
      </div>
    </template>

    <!-- Rescheduling -->
    <template v-if="isBeingRescheduled">
      <div class="re-assign-insctruction">
        <template v-if="rescheduleForm.step === 1">Select Appointment Type</template>
        <template v-if="rescheduleForm.step === 2">Select New Date</template>
        <template v-if="rescheduleForm.step === 3">Select Reviewer</template>
        <template v-if="rescheduleForm.step === 4">Update Information</template>
        <template v-if="rescheduleForm.step === 5">Confirm</template>
      </div>
      <div class="modal-content select-time">

        <!-- Appointment Type -->
        <template v-if="rescheduleForm.step === 1">
          <label class="av-label">Same Appointment Type:</label>
          <div class="appointment-type-selection" @click="selectAppointmentType(appointment.appointmentType)">
            <h5>{{ appointment.appointmentType.name }}</h5>
          </div>
          <label class="av-label">Reschedule As Task:</label>
          <div
            v-for="appointmentType of appointmentTypes"
            :key="appointmentType.id"
            class="appointment-type-selection"
            @click="selectAppointmentType(appointmentType)"
          >
            <h5>{{ appointmentType.name }}</h5>
          </div>
        </template>

        <!-- Select Appointment Time -->
        <template v-if="rescheduleForm.step === 2 && !rescheduleForm.appointmentType.isTask">
          <appointment-slot-selection
            :appointmentTypeId="appointment.appointmentType.id"
            :maxDate="endOfDayDeadline"
            :startTime="appointment.start"
            :forReschedule="true"
            :currentReviewerId="appointment.reviewer.id"
            :internalScheduling="true"
            @slot-selected="handleSlotSelection"
          ></appointment-slot-selection>
        </template>

        <!-- Select Task Date -->
        <template v-if="rescheduleForm.step === 2 && rescheduleForm.appointmentType.isTask">
          <div class="review-date-picker"></div>
        </template>

        <!-- Select Task Reviewer -->
        <template v-if="rescheduleForm.step === 3">
          <div class="reassign-selection" v-for="reviewer of sortedAvailableReviewers" :key="reviewer.id" @click="selectReschedulerReviewer(reviewer)">
            <div class="reviewer-info">
              <span>{{ reviewer.firstName + ' ' + reviewer.lastName }}</span>
              <span class="workload-ind">{{ workloadInHours(reviewer.workload) }}</span>
            </div>
            <div class="reassign-warnings">
              <p class="no-availability" v-if="!reviewer.availableForSlot"><i class="fal fa-exclamation-circle"></i> This reviewer does not have regular available for this review and time.</p>
              <p class="conflicts" v-if="reviewer.hasExternalConflicts"><i class="fal fa-exclamation-triangle"></i> This reviewer has conflicts at this time.</p>
            </div>
          </div>
        </template>

        <!-- Intake Form -->
        <template v-if="rescheduleForm.step === 4">
          <dynamic-field
            v-for="inputDef of rescheduleForm.additionalFields"
            :key="inputDef.label"
            :inputDefinition="inputDef"
            v-model="rescheduleForm.intakeFormResponses[inputDef.id].value"
            @update-validity="updateValid($event, inputDef.id)"
          ></dynamic-field>
        </template>

        <!-- Confirm -->
        <template v-if="rescheduleForm.step === 5">
          <div class="av-form-field">
            <label class="av-label">Date</label>
            <p>{{ formatTime(rescheduleForm.start, 'MMM do yyyy hh:mm:aa') }}</p>
          </div>
          <div class="av-form-field">
            <label class="av-label">Reviewer</label>
            <p>{{ rescheduleForm.reviewer.firstName }} {{ rescheduleForm.reviewer.lastName }}</p>
          </div>
        </template>
      </div>
      <div class="modal-actions">
        <a v-if="!reassignUser" class="shaded red" @click="resetReschedule">Cancel</a>
        <a v-if="rescheduleForm.step === 4" class="shaded black" :class="{ disabled: !isRescheduleFormValid }" @click="rescheduleForm.step = 5">Next</a>
        <a v-if="rescheduleForm.step === 5" class="shaded green" :class="{ disabled: !isActive }" @click="reschedule">Reschedule</a>
      </div>
    </template>
  </av-modal>
</template>

<script lang="ts">
import { endOfDay, format, startOfDay } from 'date-fns'
import Litepicker from 'litepicker'
import { defineComponent, PropType } from 'vue'
import { mapGetters, mapState } from 'vuex'
import AppointmentService from '../common/appointmentService'
import AppointmentTypeService from '../common/appointmentTypeService'
import TimeService from '../common/timeService'
import Appointment from '../models/appointment'
import { AppointmentType, AppointmentTypeDetail } from '../models/appointmentType'
import AvailabilitySlot from '../models/availabilitySlot'
import { DynamicInput } from '../models/dynamicInput'
import DynamicField from '../components/DynamicField.vue'
import User from '../models/user'
import UserReassign from '../models/userReassign'
import AppointmentSlotSelection from './AppointmentSlotSelection.vue'
import { cloneDeep } from 'lodash'
import IntakeForm from './IntakeForm.vue'
import FormattedIntakeFormResponse from '../models/formattedIntakeFormResponse'
import addDays from 'date-fns/addDays'
import { propsToAttrMap } from '@vue/shared'

export default defineComponent({
  name: 'AppointmentInfoModal',

  props: {
    appointment: {
      type: Object as PropType<Appointment>,
      required: true
    },
    visible: {
      type: Boolean,
      required: true,
      default: false
    }
  },

  data () {
    return {
      isActive: true,

      // list of available reviewers
      availableReviewers: [] as Array<UserReassign>,
      // List of appointment types
      appointmentTypes: [] as Array<AppointmentType>,
      isBeingReassigned: false,
      isBeingRescheduled: false,
      // reassign user
      reassignUser: null as unknown as User,
      // rescheduling form
      rescheduleForm: {
        step: 1,
        appointmentType: null as any,
        appointmentTypeOfferingId: 0,
        start: null as unknown as Date,
        reviewer: null as unknown as User,
        selectLeastBusyReviewer: false,
        additionalFields: [] as Array<DynamicInput>,
        intakeFormResponses: {},
        intakeFormDifferences: false,
        isActive: true
      },
      intakeForm: null as any,
      newIntakeFormResponse: { isValid: true } as unknown as FormattedIntakeFormResponse,
      isEditingAppointmentForm: false
    }
  },

  mounted () {
    /**
     * get all appointment types
     */
    AppointmentTypeService.getAll().then(res => {
      this.appointmentTypes = res.data.filter(at => at.isTask)
    })
  },

  emits: ['close', 'appointment-cancelled', 'appointment-reassigned', 'appointment-rescheduled', 'appointment-updated'],

  watch: {
    visible: {
      handler (app) {
        this.resetReassign()
        this.resetReschedule()
        this.availableReviewers = []

        if (app) {
          // this.getAvailableReviewers()
        }
      },
      immediate: true
    }
  },

  computed: {
    endOfDayDeadline (): Date {
      return addDays(this.appointment.reviewDeadline, 1)
    },

    /**
     * Sorts available reviewers by workload
     */
    sortedAvailableReviewers (): Array<UserReassign> {
      const reviewers = cloneDeep(this.availableReviewers)
        .sort((a, b) => {
          if (a.workload > b.workload) {
            return 1
          } else if (a.workload < b.workload) {
            return -1
          } else {
            return 0
          }
        })

      if (this.isAdmin) {
        return reviewers
      } else {
        return reviewers.filter(r => !r.hasExternalConflicts && r.availableForSlot)
      }
    },

    /**
     * determines if reschedule form is valid
     */
    isRescheduleFormValid (): boolean {
      const responses = Object.values(this.rescheduleForm.intakeFormResponses)
      return responses.reduce((previous: boolean, current: any) => {
        return previous && current.valid
      }, true) as boolean
    },

    formattedIntakeFormResponse (): FormattedIntakeFormResponse {
      const defaultResponses = {
        pIFirstName: { value: this.appointment.piFirstName },
        pILastName: { value: this.appointment.piLastName },
        pIEmail: { value: this.appointment.piEmail },
        pIPhone: { value: this.appointment.piPhone },
        proposalNumberType: { value: this.appointment.proposalNumberType },
        proposalNumber: { value: this.appointment.proposalNumber }
      }

      return {
        defaultResponses: defaultResponses,
        intakeFormResponses: this.appointment.intakeFormResponse,
        additionalContacts: this.appointment.additionalContacts,
        isValid: true
      }
    },

    /**
     * The display value for the proposal number formatted to include the
     * proposal number type
     */
    proposalNumberDisplay (): string {
      return AppointmentService.formatProposalNumber(this.appointment)
    },

    ...mapState(['userId']),
    ...mapGetters(['isAdmin', 'isReviewer'])
  },

  methods: {
    saveIntakeForm () {
      const params = {
        intakeFormResponse: this.newIntakeFormResponse.intakeFormResponses,
        additionalContacts: this.newIntakeFormResponse.additionalContacts
      }

      const globalResponses = cloneDeep(Object.values(this.newIntakeFormResponse.defaultResponses))
      globalResponses.forEach(gr => {
        params[gr.id] = gr.value
      })

      AppointmentService.updateIntakeForm(this.appointment.id, params).then(res => {
        this.$emit('appointment-updated', res.data)
        this.isEditingAppointmentForm = false
      })
    },

    /**
     * Prepares the appointment intake form for editing
     */
    beginEditingIntakeForm () {
      AppointmentService.getIntakeForm(this.appointment.id).then(res => {
        this.intakeForm = res.data
        this.isEditingAppointmentForm = true
      })
    },

    /**
     * workload in hours conversion
     */
    workloadInHours (workload: number): string {
      const hours = Math.floor(workload / 60)
      const minutes = workload - (hours * 60)
      return `${hours > 0 ? hours : '0'}${hours === 1 ? 'hr' : 'hrs'} ${minutes}min`
    },

    /**
     * reschedule appointment
     */
    reschedule () {
      this.isActive = false
      const params = {} as any
      params.startTime = this.rescheduleForm.start

      params.reviewerId = this.rescheduleForm.reviewer.id
      params.appointmentTypeId = this.rescheduleForm.appointmentType.id
      if (this.rescheduleForm.appointmentTypeOfferingId !== 0) {
        params.appointmentTypeOfferingId = this.rescheduleForm.appointmentTypeOfferingId
      }

      const allFields = cloneDeep(this.appointment.intakeFormResponse)
      const newFields = cloneDeep(Object.values(this.rescheduleForm.intakeFormResponses))
      newFields.forEach((r: any) => {
        if (r.type === 'checkbox group') {
          r.value = r.value.join(', ')
        }
        allFields.push(r)
      })
      params.intakeFormResponse = allFields

      AppointmentService.rescheduleAppointment(this.appointment.id, params).then(res => {
        this.resetReschedule()
        this.isBeingRescheduled = false
        res.data.start = new Date(res.data.start)
        res.data.end = new Date(res.data.end)
        res.data.scheduledOn = new Date(res.data.scheduledOn)
        res.data.reviewDeadline = new Date(res.data.reviewDeadline)
        this.$emit('appointment-rescheduled', res.data)
        this.isActive = true
      })
    },

    /**
     * validates intake form
     */
    updateValid (val, id) {
      this.rescheduleForm.intakeFormResponses[id].valid = val
    },

    /**
     * build new intake form
     */
    constructNewIntakeForm (appointmentType: AppointmentTypeDetail) {
      const diff = appointmentType.currentForm.inputs.filter(input => {
        return this.appointment.intakeFormResponse.find(r => r.label === input.label) == null
      })

      if (diff.length === 0) {
      } else {
        this.rescheduleForm.additionalFields = diff
        const intakeFormResponse = {}
        diff.forEach((input: DynamicInput) => {
          intakeFormResponse[input.id] = {
            id: input.id,
            type: input.type,
            label: input.label,
            value: '',
            required: input.required,
            valid: false
          }

          if (input.type === 'radio') {
            intakeFormResponse[input.id].value = input.options[0]
          }

          if (input.type === 'checkbox group') {
            intakeFormResponse[input.id].value = []
          }
        })
        this.rescheduleForm.intakeFormResponses = intakeFormResponse
        this.rescheduleForm.intakeFormDifferences = true
      }
    },

    /**
     * selects a reviewer for rescheduled appointment
     */
    selectReschedulerReviewer (reviewer: User) {
      this.rescheduleForm.reviewer = reviewer

      if (this.rescheduleForm.appointmentType.isTask) {
        if (this.rescheduleForm.additionalFields.length === 0) {
          this.rescheduleForm.step = 5
        } else {
          this.rescheduleForm.step = 4
        }
      } else {
        this.rescheduleForm.step = 5
      }
    },

    /**
     * handle slot selection
     */
    handleSlotSelection (slot: AvailabilitySlot) {
      this.rescheduleForm.start = slot.start
      this.rescheduleForm.appointmentTypeOfferingId = slot.appointmentTypeOffering.id

      // REFACTOR HERE
      if (!slot.reviewer) {
        AppointmentService.reviewerAvailability(slot.appointmentTypeOffering.id, slot.start).then(res => {
          this.availableReviewers = res.data
          this.rescheduleForm.step = 3
        })
      } else {
        this.rescheduleForm.reviewer = slot.reviewer
        this.rescheduleForm.step = 5
      }
    },

    /**
     * reset reschedule form
     */
    resetReschedule () {
      this.rescheduleForm = {
        step: 1,
        appointmentType: null as any,
        appointmentTypeOfferingId: 0,
        start: null as unknown as Date,
        reviewer: null as unknown as User,
        additionalFields: [],
        selectLeastBusyReviewer: !this.isAdmin,
        intakeFormResponses: {} as object,
        intakeFormDifferences: false,
        isActive: true
      }

      this.isBeingRescheduled = false
    },

    /**
     * select appointment type
     */
    selectAppointmentType (appointentType: AppointmentTypeDetail) {
      this.rescheduleForm.appointmentType = appointentType

      if (this.appointment.appointmentType.id !== appointentType.id) {
        this.constructNewIntakeForm(appointentType)
      }

      if (this.rescheduleForm.appointmentType.isTask) {
        setTimeout(() => {
          const picker = new Litepicker({ element: document.querySelector('.review-date-picker'), inlineMode: true, singleMode: true, onSelect: this.handleReviewDateSelection, disableWeekends: true })
          picker.show()
        }, 0)
      }

      this.rescheduleForm.step = 2
    },

    /**
     * handle review date selection
     */
    handleReviewDateSelection (date: Date) {
      this.rescheduleForm.start = date
      if (this.isAdmin) {
        AppointmentService.reviewerAvailabilityForDate(date).then(res => {
          this.availableReviewers = res.data
          this.rescheduleForm.step = 3
        })
      } else {
        this.rescheduleForm.reviewer = this.appointment.reviewer
        this.rescheduleForm.step = 5
      }
    },

    /**
     * begin reassign process
     */
    beginReassign () {
      this.isBeingReassigned = true
      if (this.appointment.appointmentType.isTask) {
        AppointmentService.reviewerAvailabilityForDate(startOfDay(this.appointment.start)).then(res => {
          this.availableReviewers = res.data
        })
      } else {
        AppointmentService.reviewerAvailability(this.appointment.appointmentTypeOffering.id, this.appointment.start).then(res => {
          this.availableReviewers = res.data
        })
        // this.getAvailableReviewers =
      }
      // AppointmentService.reviewerAvailabilityForDate(this.appointment.start)
    },

    /**
     * cancel appointment
     */
    cancel () {
      if (window.confirm('Are you sure?')) {
        AppointmentService.cancelAppointment(this.appointment.id).then(res => {
          this.$emit('close')
          this.$emit('appointment-cancelled', this.appointment.id)
        })
      }
    },

    /**
     * close form
     */
    close () {
      this.isEditingAppointmentForm = false
      this.$emit('close')
    },

    /**
     * Formates the given number of minutes into a human readable time
     */
    formatMinutes (minutes: number): string {
      return TimeService.minutesToHoursFormatted(minutes)
    },

    /**
     * takes a date returns a string based on given format
     */
    formatTime (date: Date, formatString: string): string {
      return format(date, formatString)
    },

    /**
     * reset reassign process
     */
    resetReassign () {
      this.isBeingReassigned = false
      this.reassignUser = null as unknown as User
    },

    /**
     * reassign appointment
     */
    reassign () {
      this.isActive = false
      AppointmentService.reassign(this.appointment.id, this.reassignUser.id).then(res => {
        this.$emit('appointment-reassigned', res.data)
        this.isActive = true
      })
    }
  },

  components: {
    IntakeForm,
    DynamicField,
    AppointmentSlotSelection
  }
})
</script>
